import type { AssetLoader } from "@getpresenta/renderer";
import instance from "lib/axiosInterceptor";
import { baseUrl } from "utils/baseUrl";
import { getToken } from "utils/localStorageServices";
import { getPublicDocumentStreamUrl } from "./publicDocumentStream";

/** Authenticated renderer media loader with explicit object-URL cleanup. */
export interface RendererAssetLoader extends AssetLoader {
  /** Invalidates pending loads and revokes the temporary URLs owned by this loader. */
  dispose: () => void;
}

/**
 * Loads document streams through the existing authenticated API client.
 * Only the configured API's stream endpoint receives credentials. Requests are
 * shared within the current session, retried after failures, and invalidated
 * when the token changes. External media keeps its original URL.
 */
export function createRendererAssetLoader(): RendererAssetLoader {
  let apiBase: URL | null = null;
  try {
    if (baseUrl) apiBase = new URL(String(baseUrl).replace(/\/+$/, "") + "/");
  } catch {
    // An unconfigured API must never cause credentials to reach another host.
  }
  const streamPath = apiBase
    ? new URL("Document/stream/", apiBase).pathname.toLowerCase()
    : "";
  const pending = new Map<string, Promise<string>>();
  const objectUrls = new Set<string>();
  let sessionToken = getToken();
  let generation = 0;
  let disposed = false;

  const clear = () => {
    generation += 1;
    pending.clear();
    objectUrls.forEach((uri) => URL.revokeObjectURL(uri));
    objectUrls.clear();
  };
  const syncSession = () => {
    const nextToken = getToken();
    if (nextToken !== sessionToken) {
      clear();
      sessionToken = nextToken;
    }
  };
  const resolveStream = (uri: string): string | undefined => {
    if (!apiBase) return undefined;
    try {
      const relative = uri.startsWith("/api/tweek/")
        ? uri.slice("/api/tweek/".length)
        : uri;
      const parsed = new URL(relative, apiBase);
      if (parsed.origin !== apiBase.origin || parsed.username || parsed.password) return undefined;
      if (!parsed.pathname.toLowerCase().startsWith(streamPath)) return undefined;
      Array.from(parsed.searchParams.keys()).forEach((key) => {
        if (key.toLowerCase() === "presentationid") parsed.searchParams.delete(key);
      });
      return getPublicDocumentStreamUrl(parsed.href);
    } catch {
      return undefined;
    }
  };

  return {
    handlesUri: (uri) => Boolean(resolveStream(uri)),
    loadAssetUri: ({ uri, accept }) => {
      const stream = resolveStream(uri);
      if (!stream) return Promise.resolve(uri);
      if (disposed) return Promise.reject(new Error("Renderer asset loader was released."));
      syncSession();
      const cached = pending.get(stream);
      if (cached) return cached;
      const startedGeneration = generation;
      const request = Promise.resolve().then(async () => {
        syncSession();
        if (disposed || generation !== startedGeneration) throw new Error("Media session changed.");
        const response = await instance.get<Blob>(stream, {
          responseType: "blob",
          headers: { Accept: accept },
          skipAuthRedirect: true,
        });
        syncSession();
        if (disposed || generation !== startedGeneration) throw new Error("Media session changed.");
        if (!(response.data instanceof Blob) || response.data.size === 0) {
          throw new Error("The document stream did not return media data.");
        }
        const objectUrl = URL.createObjectURL(response.data);
        objectUrls.add(objectUrl);
        return objectUrl;
      });
      pending.set(stream, request);
      void request.catch(() => {
        if (pending.get(stream) === request) pending.delete(stream);
      });
      return request;
    },
    dispose: () => {
      disposed = true;
      clear();
    },
  };
}
